// src/services/api/creditsApi.js
//
// Read endpoints for the Tenant Credits dashboard (shipped 2026-06-02):
//   GET /api/v1/credits/balance              — this tenant's balance for the plan window
//   GET /api/v1/credits/usage?group_by=user  — per-user usage breakdown
//   GET /api/v1/credits/usage?group_by=tenant — PB5: the CUSTOMER's wallet split per
//                                              workspace. Customer-admin-only since
//                                              2026-08-11: a plain member or tenant-admin
//                                              gets 403 CUSTOMER_ADMIN_REQUIRED.
//
// Conventions mirror statsApi.js / runsApi.js:
//   - suppressTenantHeader is CONDITIONAL on `tenant` being supplied. Member /
//     tenant-admin callers rely on X-Tenant-Id from localStorage; superadmin
//     drill-downs pass an explicit ?tenant= which must win over any stale header.
//   - Callers that know the active tenant pass `tenant` to keep the URL
//     tenant-keyed — the BE serves Cache-Control: private, max-age=60 keyed by
//     URL only, so an unscoped URL could leak the previous tenant's numbers
//     after a switch within 60s.
//
// /balance is the WALLET only — which output types a plan sells is a separate
// read (entitlementsApi.js). Payloads are passed through VERBATIM: this module
// never reshapes them.
//
// IMPORTANT: every credit/cost field on the wire is a Decimal STRING (lossless).
// Parse with utils/creditsFormat.js before arithmetic — never assume numbers.
//
// Errors thrown here carry `code` (error_code) and `status` so callers branch on
// the code — never on the message, which is localised.
//
import i18n from '../../i18n/index.js';
import { apiFetch } from '../../auth/apiFetch.js';

const CREDITS = '/api/v1/credits';

// Only `user` and `tenant` are valid; anything else (e.g. `day`) is 422
// INVALID_GROUP_BY on the server, so it is refused here before a request is made.
const GROUP_BY = new Set(['user', 'tenant']);

// The integration doc describes the error envelope as { detail: { error_code,
// message } }, but the rest of this FE (apiFetch.js, statsApi.js, adminApi.js)
// reads error_code/message at the TOP level — implying FastAPI flattens them.
// Parse tolerantly so we surface the friendly message whichever shape arrives.
async function readError(res) {
  const body = await res.json().catch(() => ({}));
  return body && body.detail && typeof body.detail === 'object' ? body.detail : body;
}

function structuredError(message, envelope, status) {
  const e = new Error(message);
  e.code = envelope?.error_code || '';
  e.status = status;
  return e;
}

/**
 * @param {{tenant?: string, fresh?: boolean}} [opts]
 *
 * ⭐ `fresh` bypasses the BROWSER cache for this one read. /balance is served
 * `Cache-Control: private, max-age=60`, which is right for ordinary views and
 * wrong for a POLL: fetch's default cache mode may satisfy every request to the
 * same URL from that stored response, so a loop watching for a change can read
 * one 60-second-old body over and over and conclude nothing happened. Both
 * pollers (the checkout return, the top-up return) pass it; nothing else should
 * — the caching is deliberate and worth keeping everywhere else.
 */
export async function fetchCreditBalance({ tenant, fresh = false } = {}) {
  const params = new URLSearchParams();
  if (tenant) params.set('tenant', tenant);
  const qs = params.toString();

  const res = await apiFetch(`${CREDITS}/balance${qs ? `?${qs}` : ''}`, {
    suppressTenantHeader: !!tenant,
    ...(fresh ? { cache: 'no-store' } : {}),
  });
  if (!res.ok) {
    const err = await readError(res);
    if (err.error_code === 'TENANT_REQUIRED') {
      throw structuredError(i18n.t('errors:credits.tenantRequired'), err, res.status);
    }
    throw structuredError(err.message || `Failed to fetch credit balance: ${res.status}`, err, res.status);
  }
  return res.json();
}

async function requestUsage(groupBy, tenant) {
  if (!GROUP_BY.has(groupBy)) {
    throw structuredError(i18n.t('errors:credits.invalidGroupBy'), { error_code: 'INVALID_GROUP_BY' }, 422);
  }
  const params = new URLSearchParams();
  params.set('group_by', groupBy);
  if (tenant) params.set('tenant', tenant);

  const res = await apiFetch(`${CREDITS}/usage?${params}`, {
    suppressTenantHeader: !!tenant,
  });
  if (!res.ok) {
    const err = await readError(res);
    if (err.error_code === 'INVALID_GROUP_BY') {
      throw structuredError(i18n.t('errors:credits.invalidGroupBy'), err, res.status);
    }
    if (err.error_code === 'UNKNOWN_QUERY_PARAM') {
      throw structuredError(i18n.t('errors:api.unrecognizedParam', { detail: err.message || res.status }), err, res.status);
    }
    // 403 CUSTOMER_ADMIN_REQUIRED (group_by=tenant only) is thrown with its code
    // and NO keyed copy on purpose: the caller must treat it as "not for this
    // user" and hide the panel, never render it. apiFetch has already noted
    // permissions drift if the local claims disagreed with the refusal.
    throw structuredError(err.message || `Failed to fetch credit usage: ${res.status}`, err, res.status);
  }
  return res.json();
}

/** Per-user usage for the tenant (every member may read it). */
export async function fetchCreditUsage({ tenant } = {}) {
  return requestUsage('user', tenant);
}

/**
 * PB5: the customer's wallet itemised per workspace. Customer-admin-only —
 * 403 CUSTOMER_ADMIN_REQUIRED for a member or tenant-admin (err.code). A caller
 * on a tenant with NO customer gets 200 with a single bucket (that tenant's own
 * spend): render it, do not special-case it.
 */
export async function fetchWalletSplit({ tenant } = {}) {
  return requestUsage('tenant', tenant);
}
